import {
  ScreenerDimension,
  type ScreenerReq,
  type ScreenerReqCompanyInfoFilters,
  type ScreenerReqPagination,
  type ScreenerReqSegments,
} from '@revelio/data-access';

import type { ScreenerMetricValues } from '../../components/PageConfigProvider/types/metric';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
  type ListFilter,
  type PrimaryFilterId,
  type StaticListFilter,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';
import { getScreenerSegmentStartValue } from './get-screener-segment-start-value';
import { getStaticListBoolean } from './getStaticListBoolean';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';

export const buildScreenerFilters = ({
  effectiveFilters,
  primaryDimension,
  metric,
  pagination,
  sorting,
}: {
  effectiveFilters: Filter[];
  primaryDimension: PrimaryFilterId;
  metric: ScreenerMetricValues;
  pagination: ScreenerReqPagination;
  sorting?: ScreenerReq['sorting'];
}): ScreenerReq => {
  const find = (id: FilterId) => effectiveFilters.find((f) => f.id === id);

  const geography = find(FilterId.GEOGRAPHY);
  const company = find(FilterId.COMPANY);
  const industry = find(FilterId.INDUSTRY);
  const role = find(FilterId.ROLE);
  const contingentWorkers = find(FilterId.CONTINGENT_WORKERS);

  const primary_selector = (() => {
    switch (primaryDimension) {
      case FilterId.COMPANY: {
        // Check both COMPANY and INDUSTRY filters for rics codes
        const filterToUse = industry || company;
        const { rics_k10, rics_k400 } = filterToUse
          ? getV2CompanyFilters(filterToUse)
          : {};
        const acc: Record<string, number[]> = {};

        // Only include rics industries, company is not supported
        if (Array.isArray(rics_k10) && rics_k10.length) {
          acc.rics_k10 = rics_k10;
        }
        if (Array.isArray(rics_k400) && rics_k400.length) {
          acc.rics_k400 = rics_k400;
        }
        return acc;
      }
      case FilterId.GEOGRAPHY: {
        const geo = geography ? getV2GeographyFilters(geography) : {};
        const acc: Record<string, number[]> = {};
        if (geo.country?.length) {
          acc.country = geo.country;
        }
        if (geo.region?.length) {
          acc.region = geo.region;
        }
        if (geo.msa?.length) {
          acc.metro_area = geo.msa;
        }
        if (geo.state?.length) {
          acc.state = geo.state;
        }
        return acc;
      }
      case FilterId.ROLE: {
        const acc: Record<string, number[]> = {};
        if (role?.type === FilterType.TREE) {
          // Include job_category (role_k7) and role_k150 for screener queries
          role.selections?.forEach((s: TreeSelection) => {
            const id = getNumericIdFromNestedId(s.id);
            if (id === null) return;
            if (s.levelId === 'role_k7') {
              acc.job_category = [...(acc.job_category || []), id];
            } else if (s.levelId === 'role_k150') {
              acc.role_k150 = [...(acc.role_k150 || []), id];
            }
          });
        }
        return acc;
      }
      default:
        return {};
    }
  })();

  const segments: Partial<ScreenerReqSegments> = {};
  const startVal = getScreenerSegmentStartValue(metric);

  // Map FilterId to segment field name
  const mapFilterIdToSegmentField = (
    filterId: FilterId
  ): keyof ScreenerReqSegments | null => {
    switch (filterId) {
      case FilterId.ATTRITION_RATE:
        return 'attrition';
      case FilterId.GROWTH_RATE:
        return 'growth_yoy';
      case FilterId.HIRING_RATE:
        return 'hiring';
      case FilterId.HEADCOUNT:
        return 'headcount';
      case FilterId.SALARY:
        return 'salary';
      case FilterId.TENURE:
        return 'tenure';
      case FilterId.INFLOW:
        return 'inflow';
      case FilterId.OUTFLOW:
        return 'outflow';
      default:
        return null;
    }
  };

  // Flatten MultiFilter instances so SegmentedFilters inside go through the flow
  const filtersToProcess = effectiveFilters.flatMap((filter) =>
    isMultiFilter(filter) ? filter.instances : [filter]
  );

  // Process SegmentedFilter: extract range filter values and process segmentations
  filtersToProcess.forEach((filter) => {
    if (isSegmentedFilter(filter)) {
      const segmented = filter;
      const wrapped = segmented.wrappedFilter;

      // Get segment field name for this filter
      const segmentField = mapFilterIdToSegmentField(filter.id);

      if (!segmentField) return; // Skip if not a segmentable filter

      const segmentFilter: Record<string, number[]> = {};
      let rangeFilterStartVal: number | undefined;
      let rangeFilterEndVal: number | undefined;

      // Handle RangeFilter wrapped filter - extract min/max for start_val/end_val
      if (wrapped.type === FilterType.RANGE) {
        const rangeFilter = wrapped;

        // Determine if this is a rate filter (needs percentage conversion)
        const isRateFilter = [
          FilterId.ATTRITION_RATE,
          FilterId.GROWTH_RATE,
          FilterId.HIRING_RATE,
        ].includes(rangeFilter.id);

        let min = rangeFilter.min;
        let max = rangeFilter.max;

        // Convert percentage values for rate filters (from percentage to decimal)
        if (isRateFilter) {
          min = min !== undefined ? min / 100 : undefined;
          max = max !== undefined ? max / 100 : undefined;
        }

        rangeFilterStartVal = min;
        rangeFilterEndVal = max;
      }

      // Process segmentations (demographic filters nested within SegmentedFilter)
      if (segmented.segmentations) {
        Object.entries(segmented.segmentations).forEach(
          ([_segmentKey, segmentFilterValue]) => {
            // Handle TreeFilter segments (skip ROLE/GEOGRAPHY - handled separately below)
            if (
              segmentFilterValue.type === FilterType.TREE &&
              segmentFilterValue.id !== FilterId.ROLE &&
              segmentFilterValue.id !== FilterId.GEOGRAPHY
            ) {
              const treeFilter = segmentFilterValue;
              treeFilter.selections?.forEach((s: TreeSelection) => {
                const id = getNumericIdFromNestedId(s.id);
                if (id === null || !s.levelId) return;
                segmentFilter[s.levelId] = [
                  ...(segmentFilter[s.levelId] || []),
                  id,
                ];
              });
            }

            // Handle ListFilter segments (ethnicity, gender, education, seniority)
            if (segmentFilterValue.type === FilterType.LIST) {
              const listFilter = segmentFilterValue;
              const selectionListId = listFilter.selectionListId;
              const ids =
                listFilter.selections
                  ?.map((s: { id: string | number }) =>
                    getNumericIdFromNestedId(String(s.id))
                  )
                  .filter(
                    (n): n is number =>
                      typeof n === 'number' && Number.isFinite(n)
                  ) || [];

              if (ids.length > 0 && selectionListId) {
                // Map selectionListId to segment key
                if (selectionListId === 'ethnicity') {
                  segmentFilter.ethnicity = ids;
                } else if (selectionListId === 'gender') {
                  segmentFilter.gender = ids;
                } else if (selectionListId === 'highest_degree') {
                  segmentFilter.highest_degree = ids;
                } else if (selectionListId === 'seniority') {
                  segmentFilter.seniority = ids;
                }
              }
            }

            // Handle company/rics from segmented filters
            if (
              segmentFilterValue.id === FilterId.COMPANY &&
              segmentFilterValue.type === FilterType.TREE
            ) {
              const { rics_k10, rics_k400 } =
                getV2CompanyFilters(segmentFilterValue);
              if (Array.isArray(rics_k10) && rics_k10.length) {
                segmentFilter.rics_k10 = rics_k10;
              }
              if (Array.isArray(rics_k400) && rics_k400.length) {
                segmentFilter.rics_k400 = rics_k400;
              }
            }

            // Handle geography from segmented filters
            if (
              segmentFilterValue.id === FilterId.GEOGRAPHY &&
              segmentFilterValue.type === FilterType.TREE
            ) {
              const geo = getV2GeographyFilters(segmentFilterValue);
              if (geo.country?.length) {
                segmentFilter.country = geo.country;
              }
              if (geo.region?.length) {
                segmentFilter.region = geo.region;
              }
              if (geo.msa?.length) {
                segmentFilter.metro_area = geo.msa;
              }
              if (geo.state?.length) {
                segmentFilter.state = geo.state;
              }
            }

            // Handle role from segmented filters
            if (
              segmentFilterValue.id === FilterId.ROLE &&
              segmentFilterValue.type === FilterType.TREE
            ) {
              const roleFilter = segmentFilterValue;
              roleFilter.selections?.forEach((s: TreeSelection) => {
                const id = getNumericIdFromNestedId(s.id);
                if (id === null) return;
                if (s.levelId === 'role_k7') {
                  segmentFilter.job_category = [
                    ...(segmentFilter.job_category || []),
                    id,
                  ];
                } else if (s.levelId === 'role_k150') {
                  segmentFilter.role_k150 = [
                    ...(segmentFilter.role_k150 || []),
                    id,
                  ];
                } else if (s.levelId) {
                  segmentFilter[s.levelId] = [
                    ...(segmentFilter[s.levelId] || []),
                    id,
                  ];
                }
              });
            }
          }
        );
      }

      // Create segment entry for this segmented filter
      if (
        Object.keys(segmentFilter).length > 0 ||
        rangeFilterStartVal !== undefined ||
        rangeFilterEndVal !== undefined
      ) {
        // For "less than" filters, start_val should be undefined (not a default value)
        // For "greater than" filters, end_val should be undefined
        // For "between" filters, both should be set
        // Only use default start_val if we have other segment filters but no range values
        const finalStartVal = (() => {
          if (rangeFilterStartVal !== undefined) {
            return rangeFilterStartVal;
          }
          if (Object.keys(segmentFilter).length > 0) {
            return getScreenerSegmentStartValue(`screener_${segmentField}`);
          }
          return undefined;
        })();

        if (!segments[segmentField]) {
          segments[segmentField] = [];
        }

        segments[segmentField].push({
          filter: segmentFilter,
          ...(finalStartVal !== undefined && { start_val: finalStartVal }),
          ...(rangeFilterEndVal !== undefined && {
            end_val: rangeFilterEndVal,
          }),
        });
      }
    }
  });

  // Fallback: Only include role in segments when role is not the primary dimension
  // (for backward compatibility with non-segmented filters)
  // Note: This creates a segment for the current metric, not for role filter
  const fallbackSegmentFilter: Record<string, number[]> = {};
  if (role && primaryDimension !== FilterId.ROLE && !isSegmentedFilter(role)) {
    if (role.type === FilterType.TREE) {
      role.selections?.forEach((s: TreeSelection) => {
        const id = getNumericIdFromNestedId(s.id);
        if (id === null) return;
        if (s.levelId === 'role_k7') {
          fallbackSegmentFilter.job_category = [
            ...(fallbackSegmentFilter.job_category || []),
            id,
          ];
        } else if (s.levelId === 'role_k150') {
          fallbackSegmentFilter.role_k150 = [
            ...(fallbackSegmentFilter.role_k150 || []),
            id,
          ];
        } else if (s.levelId) {
          fallbackSegmentFilter[s.levelId] = [
            ...(fallbackSegmentFilter[s.levelId] || []),
            id,
          ];
        }
      });
    }
  }

  // Fallback: Only include company/rics filters in segments when company is not the primary dimension
  if (
    company &&
    primaryDimension !== FilterId.COMPANY &&
    !isSegmentedFilter(company)
  ) {
    const { rics_k10, rics_k400 } = getV2CompanyFilters(company);
    if (Array.isArray(rics_k10) && rics_k10.length) {
      fallbackSegmentFilter.rics_k10 = rics_k10;
    }
    if (Array.isArray(rics_k400) && rics_k400.length) {
      fallbackSegmentFilter.rics_k400 = rics_k400;
    }
  }

  // Fallback: Only include geography filters in segments when geography is not the primary dimension
  if (
    geography &&
    primaryDimension !== FilterId.GEOGRAPHY &&
    !isSegmentedFilter(geography)
  ) {
    const geo = getV2GeographyFilters(geography);
    if (geo.country?.length) {
      fallbackSegmentFilter.country = geo.country;
    }
    if (geo.region?.length) {
      fallbackSegmentFilter.region = geo.region;
    }
    if (geo.msa?.length) {
      fallbackSegmentFilter.metro_area = geo.msa;
    }
    if (geo.state?.length) {
      fallbackSegmentFilter.state = geo.state;
    }
  }

  // Fallback: Only include list filters in segments when not already segmented
  const ethnicityFilter = find(FilterId.ETHNICITY);
  if (
    ethnicityFilter &&
    ethnicityFilter.type === FilterType.LIST &&
    !isSegmentedFilter(ethnicityFilter)
  ) {
    const ethnicity =
      (ethnicityFilter.selections || [])
        .map((s: { id: string | number }) =>
          getNumericIdFromNestedId(String(s.id))
        )
        .filter(
          (n): n is number => typeof n === 'number' && Number.isFinite(n)
        ) || [];
    if (ethnicity.length) {
      fallbackSegmentFilter.ethnicity = ethnicity;
    }
  }

  const genderFilter = find(FilterId.GENDER);
  if (
    genderFilter &&
    genderFilter.type === FilterType.LIST &&
    !isSegmentedFilter(genderFilter)
  ) {
    const gender =
      (genderFilter.selections || [])
        .map((s: { id: string | number }) =>
          getNumericIdFromNestedId(String(s.id))
        )
        .filter(
          (n): n is number => typeof n === 'number' && Number.isFinite(n)
        ) || [];
    if (gender.length) fallbackSegmentFilter.gender = gender;
  }

  const degreeFilter = find(FilterId.EDUCATION);
  if (
    degreeFilter &&
    degreeFilter.type === FilterType.LIST &&
    !isSegmentedFilter(degreeFilter)
  ) {
    const highest_degree =
      (degreeFilter.selections || [])
        .map((s: { id: string | number }) =>
          getNumericIdFromNestedId(String(s.id))
        )
        .filter(
          (n): n is number => typeof n === 'number' && Number.isFinite(n)
        ) || [];
    if (highest_degree.length) {
      fallbackSegmentFilter.highest_degree = highest_degree;
    }
  }

  // Create fallback segment for the current metric if there are fallback filters
  if (Object.keys(fallbackSegmentFilter).length > 0) {
    const metricField = metric.replace(
      'screener_',
      ''
    ) as keyof ScreenerReqSegments;
    if (!segments[metricField]) {
      segments[metricField] = [];
    }
    segments[metricField].push({
      filter: fallbackSegmentFilter,
      start_val: startVal,
    });
  }

  // Build entity_info_selectors (company_info_selectors)
  const companyInfoSelectors: ScreenerReqCompanyInfoFilters = {};

  // Competitors filter -> competitors_of
  const competitorsFilter = find(FilterId.COMPETITORS);
  if (competitorsFilter?.type === FilterType.TREE) {
    const treeFilter: TreeFilter = competitorsFilter;
    const competitorIds = treeFilter.selections
      .map((s) => getNumericIdFromNestedId(s.id))
      .filter((id): id is number => id !== null);
    if (competitorIds.length > 0) {
      companyInfoSelectors.competitors_of = competitorIds;
    }
  }

  // Externally Owned filter -> is_subsidiary
  const externallyOwnedFilter = find(FilterId.EXTERNALLY_OWNED);
  if (externallyOwnedFilter?.type === FilterType.STATIC_LIST) {
    const staticListFilter: StaticListFilter = externallyOwnedFilter;
    const subsidiaryValue = getStaticListBoolean(
      staticListFilter,
      'is_subsidiary'
    );
    if ('is_subsidiary' in subsidiaryValue) {
      companyInfoSelectors.is_subsidiary = subsidiaryValue.is_subsidiary;
    }
  }

  // Public filter -> is_public
  const publicFilter = find(FilterId.PUBLIC);
  if (publicFilter?.type === FilterType.STATIC_LIST) {
    const staticListFilter: StaticListFilter = publicFilter;
    const publicValue = getStaticListBoolean(staticListFilter, 'is_public');
    if ('is_public' in publicValue) {
      companyInfoSelectors.is_public = publicValue.is_public;
    }
  }

  // HQ Location filter -> hq_region, hq_country, hq_msa
  const hqLocationFilter = find(FilterId.HQ_LOCATION);
  if (hqLocationFilter?.type === FilterType.TREE) {
    const treeFilter = hqLocationFilter;
    const locationsByLevel: Record<string, number[]> = {};

    (treeFilter.selections || []).forEach((s: TreeSelection) => {
      const id = getNumericIdFromNestedId(s.id);
      if (id === null || !s.levelId) return;

      const levelId = s.levelId;
      const existingIds = locationsByLevel[levelId] || [];
      locationsByLevel[levelId] = [...existingIds, id];
    });

    if (locationsByLevel['region']?.length) {
      companyInfoSelectors.hq_region = locationsByLevel['region'];
    }
    if (locationsByLevel['country']?.length) {
      companyInfoSelectors.hq_country = locationsByLevel['country'];
    }
    if (locationsByLevel['msa']?.length) {
      companyInfoSelectors.hq_msa = locationsByLevel['msa'];
    }
  }

  // Founder Ethnicity filter -> founder_api, founder_white, etc.
  const founderEthnicityFilter = find(FilterId.FOUNDER_ETHNICITY);
  if (founderEthnicityFilter?.type === FilterType.LIST) {
    const listFilter: ListFilter = founderEthnicityFilter;
    const ethnicityValues = listFilter.selections
      .map((s) => getNumericIdFromNestedId(String(s.id)))
      .filter((id): id is number => id !== null);

    ethnicityValues.forEach((value) => {
      switch (value) {
        case 1:
          companyInfoSelectors.founder_white = true;
          break;
        case 2:
          companyInfoSelectors.founder_api = true;
          break;
        case 3:
          companyInfoSelectors.founder_hispanic = true;
          break;
        case 4:
          companyInfoSelectors.founder_black = true;
          break;
        case 5:
          companyInfoSelectors.founder_native = true;
          break;
        case 6:
          companyInfoSelectors.founder_multiple = true;
          break;
      }
    });
  }

  // Founder Gender filter -> founder_male, founder_female
  const founderGenderFilter = find(FilterId.FOUNDER_GENDER);
  if (founderGenderFilter?.type === FilterType.LIST) {
    const listFilter: ListFilter = founderGenderFilter;
    const genderValues = listFilter.selections
      .map((s) => getNumericIdFromNestedId(String(s.id)))
      .filter((id): id is number => id !== null);

    genderValues.forEach((value) => {
      switch (value) {
        case 1:
          companyInfoSelectors.founder_male = true;
          break;
        case 2:
          companyInfoSelectors.founder_female = true;
          break;
      }
    });
  }

  // Founder Previous Company filter -> founder_prev_company
  const founderCompanyFilter = find(FilterId.FOUNDER_COMPANY);
  if (founderCompanyFilter?.type === FilterType.TREE) {
    const companyIds = founderCompanyFilter.selections
      .map((s) => getNumericIdFromNestedId(s.id))
      .filter((id): id is number => id !== null);
    if (companyIds.length > 0) {
      companyInfoSelectors.founder_prev_company = companyIds;
    }
  }

  // Founder Previous School filter -> founder_prev_university
  const founderSchoolFilter = find(FilterId.FOUNDER_PREVIOUS_SCHOOL);
  if (founderSchoolFilter?.type === FilterType.SCHOOL) {
    const schoolIds = Object.keys(founderSchoolFilter.schoolResultItems || {})
      .map((id) => parseInt(id, 10))
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      .filter((id) => !isNaN(id));
    if (schoolIds.length > 0) {
      companyInfoSelectors.founder_prev_university = schoolIds;
    }
  }

  // Funding Stage filter -> funding_stage
  const fundingStageFilter = find(FilterId.FUNDING_STAGE);
  if (fundingStageFilter?.type === FilterType.STATIC_LIST) {
    const staticListFilter: StaticListFilter = fundingStageFilter;
    const stageValues = staticListFilter.selections
      .map((s) => parseInt(String(s.id), 10))
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      .filter((id) => !isNaN(id));
    if (stageValues.length > 0) {
      companyInfoSelectors.funding_stage = stageValues;
    }
  }

  // Last Funding Amount filter -> last_funding_amount
  const lastFundingAmountFilter = find(FilterId.LAST_FUNDING_AMOUNT);
  if (lastFundingAmountFilter?.type === FilterType.RANGE) {
    const rangeFilter = lastFundingAmountFilter;
    if (rangeFilter.min !== undefined || rangeFilter.max !== undefined) {
      companyInfoSelectors.last_funding_amount = [
        {
          start_val: rangeFilter.min ?? 0,
          end_val: rangeFilter.max ?? Number.MAX_SAFE_INTEGER,
        },
      ];
    }
  }

  // Last Funding Year filter -> last_funding_year
  const lastFundingYearFilter = find(FilterId.LAST_FUNDING_YEAR);
  if (lastFundingYearFilter?.type === FilterType.RANGE) {
    const rangeFilter = lastFundingYearFilter;
    if (rangeFilter.min !== undefined || rangeFilter.max !== undefined) {
      companyInfoSelectors.last_funding_year = [
        {
          ...(rangeFilter.min !== undefined && { start_val: rangeFilter.min }),
          ...(rangeFilter.max !== undefined && {
            end_val: rangeFilter.max,
          }),
        },
      ];
    }
  }

  // Total Funding Amount filter -> total_funding_amount
  const totalFundingAmountFilter = find(FilterId.TOTAL_FUNDING_AMOUNT);
  if (totalFundingAmountFilter?.type === FilterType.RANGE) {
    const rangeFilter = totalFundingAmountFilter;
    if (rangeFilter.min !== undefined || rangeFilter.max !== undefined) {
      companyInfoSelectors.total_funding_amount = [
        {
          start_val: rangeFilter.min ?? 0,
          end_val: rangeFilter.max ?? Number.MAX_SAFE_INTEGER,
        },
      ];
    }
  }

  // Year Founded filter -> year_founded
  const yearFoundedFilter = find(FilterId.YEAR_FOUNDED);
  if (yearFoundedFilter?.type === FilterType.RANGE) {
    const rangeFilter = yearFoundedFilter;
    if (rangeFilter.min !== undefined || rangeFilter.max !== undefined) {
      companyInfoSelectors.year_founded = [
        {
          ...(rangeFilter.min !== undefined && { start_val: rangeFilter.min }),
          ...(rangeFilter.max !== undefined && {
            end_val: rangeFilter.max,
          }),
        },
      ];
    }
  }

  // Valuation filter -> valuation
  const valuationFilter = find(FilterId.VALUATION);
  if (valuationFilter?.type === FilterType.RANGE) {
    const rangeFilter = valuationFilter;
    if (rangeFilter.min !== undefined || rangeFilter.max !== undefined) {
      companyInfoSelectors.valuation = [
        {
          start_val: rangeFilter.min ?? 0,
          end_val: rangeFilter.max ?? Number.MAX_SAFE_INTEGER,
        },
      ];
    }
  }

  // Keywords filter -> keywords
  const keywordsFilter = find(FilterId.KEYWORDS);
  if (keywordsFilter?.type === FilterType.FREE_TEXT) {
    const freeTextFilter = keywordsFilter;
    if (freeTextFilter.text && freeTextFilter.text.length > 0) {
      companyInfoSelectors.keywords = freeTextFilter.text.map((t) =>
        t.toLowerCase()
      );
    }
  }

  const dim: ScreenerDimension = (() => {
    switch (primaryDimension) {
      case FilterId.COMPANY:
        return ScreenerDimension.Company;
      case FilterId.GEOGRAPHY:
        return ScreenerDimension.Geography;
      case FilterId.ROLE:
        return ScreenerDimension.Role;
      default:
        return ScreenerDimension.Company;
    }
  })();

  const contingentWorkersValue = contingentWorkers
    ? getStaticListBoolean(contingentWorkers, 'contingent_workers')
    : {};

  const entity_info_selectors =
    Object.keys(companyInfoSelectors).length > 0
      ? { company_info_selectors: companyInfoSelectors }
      : {};

  const req: ScreenerReq = {
    dim,
    primary_selector,
    segments,
    pagination,
    entity_info_selectors,
    ...contingentWorkersValue,
    ...(sorting ? { sorting } : {}),
  };

  return req;
};
